import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { FormDialog } from '@/components/common/FormDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import { RegistrationPlate } from '@/components/common/RegistrationPlate';
import { formatCentsForDisplay } from '@/utils/money';
import { useNotify } from '@/hooks/useNotify';
import { describeApiError } from '@/lib/describeApiError';
import {
  openCourtesyLoanPdf,
  useHandoverCourtesyLoan,
  useReturnCourtesyLoan,
  useUpdateCourtesyLoan,
  type CourtesyLoan,
} from '@/hooks/useCourtesyVehicles';
import { CourtesyLoanStatusChip } from './CourtesyLoanStatusChip';
import { CourtesyLoanPhotos } from './CourtesyLoanPhotos';
import { FuelEighthsSlider } from './FuelEighthsSlider';

export type CourtesyLoanDialogMode = 'view' | 'handover' | 'return';

interface CourtesyLoanDialogProps {
  open: boolean;
  loan: CourtesyLoan | null;
  mode: CourtesyLoanDialogMode;
  onClose: () => void;
  canUpdate?: boolean;
  onRequestMode?: (mode: CourtesyLoanDialogMode) => void;
}

export function CourtesyLoanDialog({
  open,
  loan,
  mode,
  onClose,
  canUpdate = false,
  onRequestMode,
}: CourtesyLoanDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const handoverMutation = useHandoverCourtesyLoan();
  const returnMutation = useReturnCourtesyLoan();
  const updateMutation = useUpdateCourtesyLoan();

  const [mileage, setMileage] = useState('');
  const [fuelEighths, setFuelEighths] = useState(8);
  const [notes, setNotes] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  useEffect(() => {
    if (!open || !loan) return;
    if (mode === 'return') {
      setMileage(loan.returnMileage != null ? String(loan.returnMileage) : '');
      setFuelEighths(loan.returnFuelEighths ?? loan.outFuelEighths ?? 8);
      setNotes(loan.returnNotes ?? '');
    } else {
      setMileage(loan.outMileage != null ? String(loan.outMileage) : '');
      setFuelEighths(loan.outFuelEighths ?? 8);
      setNotes(loan.outNotes ?? '');
    }
    setIsDirty(false);
  }, [loan, mode, open]);

  const mileageNumber = Number.parseInt(mileage, 10);
  const mileageValid = Number.isInteger(mileageNumber) && mileageNumber >= 0;
  const isLoading =
    handoverMutation.isPending || returnMutation.isPending || updateMutation.isPending;

  const handlePdf = async () => {
    if (!loan) return;
    try {
      await openCourtesyLoanPdf(loan.id);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('courtesy.pdfFailed') }));
    }
  };

  const handleSubmit = async () => {
    if (!loan || !mileageValid) return;
    const data: { mileage: number; fuelEighths: number; notes?: string } = {
      mileage: mileageNumber,
      fuelEighths,
    };
    const trimmed = notes.trim();
    if (trimmed.length > 0) data.notes = trimmed;
    try {
      if (mode === 'handover') {
        await handoverMutation.mutateAsync({ id: loan.id, data });
        notify.success(
          t('courtesy.handedOver'),
          t('courtesy.handedOverDesc', { plate: loan.registrationNumber }),
        );
      } else {
        await returnMutation.mutateAsync({ id: loan.id, data });
        notify.success(
          t('courtesy.returned'),
          t('courtesy.returnedDesc', { plate: loan.registrationNumber }),
        );
      }
      onClose();
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('courtesy.saveFailed') }));
    }
  };

  const handleCancel = async () => {
    if (!loan) return;
    try {
      await updateMutation.mutateAsync({ id: loan.id, data: { status: 'cancelled' } });
      notify.success(
        t('courtesy.cancelled'),
        t('courtesy.cancelledDesc', { plate: loan.registrationNumber }),
      );
      setCancelOpen(false);
      onClose();
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('courtesy.saveFailed') }));
    }
  };

  if (!loan) return null;

  const formTitle = mode === 'handover' ? t('courtesy.handoverTitle') : t('courtesy.returnTitle');

  if (mode === 'handover' || mode === 'return') {
    return (
      <FormDialog
        open={open}
        onClose={onClose}
        title={formTitle}
        isDirty={isDirty}
        onSubmit={() => { void handleSubmit(); }}
        isLoading={isLoading}
        submitDisabled={!mileageValid || isLoading}
      >
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <RegistrationPlate registrationNumber={loan.registrationNumber} />
            <TextField
              label={mode === 'handover' ? t('courtesy.outMileage') : t('courtesy.returnMileage')}
              type="number"
              value={mileage}
              onChange={(event) => {
                setMileage(event.target.value);
                setIsDirty(true);
              }}
              required
              slotProps={{ htmlInput: { min: 0, step: 1 } }}
            />
            <FuelEighthsSlider
              value={fuelEighths}
              onChange={(next) => {
                setFuelEighths(next);
                setIsDirty(true);
              }}
            />
            <TextField
              label={t('courtesy.notes')}
              value={notes}
              onChange={(event) => {
                setNotes(event.target.value);
                setIsDirty(true);
              }}
              multiline
              minRows={2}
            />
            <CourtesyLoanPhotos loanId={loan.id} />
          </Stack>
        </DialogContent>
      </FormDialog>
    );
  }

  return (
    <>
      <ResponsiveDialog
        open={open}
        onClose={onClose}
        mobileTitle={t('courtesy.loanTitle')}
        maxWidth="sm"
        fullWidth
      >
        <DialogHeader title={t('courtesy.loanTitle')} onClose={onClose} />
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <RegistrationPlate registrationNumber={loan.registrationNumber} />
              <CourtesyLoanStatusChip status={loan.status} />
              {(loan.status === 'out' || loan.status === 'returned') && (
                <Tooltip title={t('courtesy.openPdf')}>
                  <IconButton
                    onClick={() => { void handlePdf(); }}
                    aria-label={t('courtesy.openPdf')}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <PictureAsPdfIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            {loan.displayName && (
              <Typography variant="body2">{loan.displayName}</Typography>
            )}
            <Typography variant="body2">
              {t('courtesy.plannedStart')}: {new Date(loan.plannedStart).toLocaleString()}
            </Typography>
            <Typography variant="body2">
              {t('courtesy.plannedEnd')}: {new Date(loan.plannedEnd).toLocaleString()}
            </Typography>
            {loan.priceCents != null && loan.priceCents > 0 && (
              <Typography variant="body2">
                {t('courtesy.price')}: {formatCentsForDisplay(loan.priceCents)}
              </Typography>
            )}
            {(loan.status === 'out' || loan.status === 'returned') && (
              <CourtesyLoanPhotos loanId={loan.id} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {canUpdate && loan.status === 'reserved' && (
            <>
              <Button color="error" onClick={() => { setCancelOpen(true); }}>
                {t('courtesy.cancel')}
              </Button>
              <Button variant="contained" onClick={() => { onRequestMode?.('handover'); }}>
                {t('courtesy.handover')}
              </Button>
            </>
          )}
          {canUpdate && loan.status === 'out' && (
            <Button variant="contained" onClick={() => { onRequestMode?.('return'); }}>
              {t('courtesy.return')}
            </Button>
          )}
          <Button onClick={onClose}>{tc('close')}</Button>
        </DialogActions>
      </ResponsiveDialog>
      <ConfirmDialog
        open={cancelOpen}
        title={t('courtesy.cancelTitle')}
        message={t('courtesy.cancelMessage', { plate: loan.registrationNumber })}
        confirmColor="error"
        onConfirm={() => { void handleCancel(); }}
        onCancel={() => { setCancelOpen(false); }}
        loading={updateMutation.isPending}
      />
    </>
  );
}
